import { useEffect, useLayoutEffect } from "react";

// Use useLayoutEffect for SSR-critical meta updates (runs before paint)
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

interface PageSEOProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  robots?: string;
  jsonLd?: Record<string, any>;
  hreflang?: { lang: string; href: string }[];
  noindex?: boolean;
  articleMeta?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

const BASE_URL = "https://www.wavai.ae";
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";

/** Build a dynamic OG image URL via the edge function */
export function buildOgImageUrl(title: string, description?: string, path?: string): string {
  const params = new URLSearchParams();
  params.set("title", title);
  if (description) params.set("description", description);
  if (path) params.set("path", path);
  return `${SUPABASE_URL}/functions/v1/og-image?${params.toString()}`;
}

function setMeta(property: string, content: string, isOg = false) {
  const attr = isOg ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(property: string, isOg = false) {
  const attr = isOg ? "property" : "name";
  document.querySelector(`meta[${attr}="${property}"]`)?.remove();
}

function setLink(rel: string, href: string, extraAttrs?: Record<string, string>) {
  const selector = extraAttrs
    ? `link[rel="${rel}"]${Object.entries(extraAttrs).map(([k, v]) => `[${k}="${v}"]`).join("")}`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (extraAttrs) Object.entries(extraAttrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.href = href;
  return el;
}

export function usePageSEO({
  title,
  description,
  canonical,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  twitterCard = "summary_large_image",
  robots,
  jsonLd,
  hreflang,
  noindex = false,
  articleMeta,
}: PageSEOProps) {
  // Auto-generate OG image if not provided
  const resolvedOgImage = ogImage || buildOgImageUrl(ogTitle || title, ogDescription || description, canonical?.replace(BASE_URL, "") || undefined);

  useIsomorphicLayoutEffect(() => {
    // Title
    document.title = title;

    // Meta description
    setMeta("description", description);

    // Keywords
    if (keywords) setMeta("keywords", keywords);

    // Robots
    const robotsContent = noindex ? "noindex, nofollow" : (robots || "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    setMeta("robots", robotsContent);

    // Open Graph
    setMeta("og:site_name", "Wavai", true);
    setMeta("og:title", ogTitle || title, true);
    setMeta("og:description", ogDescription || description, true);
    setMeta("og:type", ogType, true);
    if (canonical) setMeta("og:url", canonical, true);
    if (resolvedOgImage) {
      setMeta("og:image", resolvedOgImage, true);
      setMeta("og:image:width", "1200", true);
      setMeta("og:image:height", "630", true);
    }

    // Article-specific OG tags
    if (articleMeta) {
      if (articleMeta.publishedTime) setMeta("article:published_time", articleMeta.publishedTime, true);
      if (articleMeta.modifiedTime) setMeta("article:modified_time", articleMeta.modifiedTime, true);
      if (articleMeta.author) setMeta("article:author", articleMeta.author, true);
      if (articleMeta.section) setMeta("article:section", articleMeta.section, true);
      articleMeta.tags?.forEach((tag, i) => {
        setMeta(`article:tag`, tag, true);
      });
    }

    // Twitter
    setMeta("twitter:card", twitterCard);
    setMeta("twitter:title", ogTitle || title);
    setMeta("twitter:description", ogDescription || description);
    if (resolvedOgImage) setMeta("twitter:image", resolvedOgImage);

    // Canonical
    const createdElements: Element[] = [];
    if (canonical) {
      const el = setLink("canonical", canonical);
      createdElements.push(el);
    }

    // Hreflang
    if (hreflang) {
      hreflang.forEach(({ lang, href }) => {
        let link = document.querySelector(`link[hreflang="${lang}"]`) as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement("link");
          link.rel = "alternate";
          link.hreflang = lang;
          document.head.appendChild(link);
          createdElements.push(link);
        }
        link.href = href;
      });
    }

    // JSON-LD
    let scriptEl: HTMLScriptElement | null = null;
    if (jsonLd) {
      scriptEl = document.querySelector('script[data-seo-jsonld]') as HTMLScriptElement | null;
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.type = "application/ld+json";
        scriptEl.setAttribute("data-seo-jsonld", "true");
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      // Cleanup hreflang links
      document.querySelectorAll("link[hreflang]").forEach((el) => el.remove());
      // Cleanup JSON-LD
      scriptEl?.remove();
      // Cleanup canonical
      document.querySelector('link[rel="canonical"]')?.remove();
      // Cleanup article meta
      if (articleMeta) {
        removeMeta("article:published_time", true);
        removeMeta("article:modified_time", true);
        removeMeta("article:author", true);
        removeMeta("article:section", true);
        removeMeta("article:tag", true);
      }
    };
  }, [title, description, canonical, keywords, ogTitle, ogDescription, resolvedOgImage, ogType, twitterCard, robots, noindex, jsonLd, hreflang, articleMeta]);
}

/** Helper to build standard bilingual hreflang + canonical */
export function buildHreflang(path: string) {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return {
    canonical: `${BASE_URL}${cleanPath}`,
    hreflang: [
      { lang: "en", href: `${BASE_URL}${cleanPath}` },
      { lang: "ar", href: `${BASE_URL}/ar${cleanPath}` },
      { lang: "x-default", href: `${BASE_URL}${cleanPath}` },
    ],
  };
}
